import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { User as SupabaseUser } from '@supabase/supabase-js'

export interface User {
  id: string
  name: string
  email: string
}

function mapSupabaseUser(su: SupabaseUser): User {
  return {
    id: su.id,
    email: su.email ?? '',
    name:
      su.user_metadata?.full_name ??
      su.user_metadata?.name ??
      su.email?.split('@')[0] ??
      'Usuario',
  }
}

async function upsertProfile(su: SupabaseUser) {
  try {
    await supabase
      .from('profiles')
      .upsert({ id: su.id, email: su.email }, { onConflict: 'id' })
  } catch (err) {
    console.error('Error upserting profile:', err)
  }
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(mapSupabaseUser(session.user))
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
      setIsLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(mapSupabaseUser(session.user))
        setIsAuthenticated(true)
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      let msg = 'Correo o contraseña incorrectos.'
      if (error.message.includes('Email not confirmed')) {
        msg = 'Debes confirmar tu correo antes de iniciar sesión.'
      }
      return { success: false as const, error: msg }
    }

    // Strict check: only succeed if we have both session and user
    if (!data.session || !data.user) {
      return {
        success: false as const,
        error: 'Correo o contraseña incorrectos.',
      }
    }

    await upsertProfile(data.user)
    return { success: true as const }
  }, [])

  const register = useCallback(
    async (
      name: string,
      email: string,
      password: string,
      accessCode: string,
    ) => {
      // 1. Validate access code against Supabase
      const { data: codeData, error: codeError } = await supabase
        .from('access_codes')
        .select('id, code, used')
        .eq('code', accessCode.trim().toUpperCase())
        .eq('used', false)
        .maybeSingle()

      if (codeError || !codeData) {
        return {
          success: false as const,
          error:
            'Este acceso no es válido. Verifica tu compra o contacta soporte.',
        }
      }

      // 2. Proceed with Supabase Auth signUp
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
        },
      })

      if (error) {
        let msg = 'Error al registrarse.'
        if (error.message.includes('User already registered')) {
          msg = 'Este email ya está registrado. Intenta iniciar sesión.'
        } else if (error.message.includes('Password should be at least')) {
          msg = 'La contraseña debe tener al menos 6 caracteres.'
        } else {
          msg = error.message
        }
        return { success: false as const, error: msg }
      }

      // 3. Mark access code as used
      await supabase
        .from('access_codes')
        .update({ used: true, email })
        .eq('id', codeData.id)

      // Always require email confirmation — never auto-login after register
      if (data.session) {
        await supabase.auth.signOut()
        setUser(null)
        setIsAuthenticated(false)
      }

      return {
        success: true as const,
        needsConfirmation: true,
        message:
          'Revisa tu correo y confirma tu cuenta antes de iniciar sesión.',
      }
    },
    [],
  )

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://codigo888.com/area-usuario/reset-password',
    })

    if (error) {
      return { success: false as const, error: error.message }
    }

    return {
      success: true as const,
      message: 'Te enviamos un correo para restablecer tu contraseña.',
    }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (error) {
      return { success: false as const, error: error.message }
    }

    return {
      success: true as const,
      message: 'Contraseña actualizada correctamente.',
    }
  }, [])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null)
    setIsAuthenticated(false)
  }, [])

  return {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    resetPassword,
    updatePassword,
    logout,
  }
}
